import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { taskApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import useProjects from "../../hooks/useProjects";
import { statusLabel } from "./activityText";
import {
  ARCHIVE_AFTER_DAYS,
  ARCHIVE_CANCELED_AFTER_DAYS,
} from "./archivePolicy";
import { displayName, relativeTime } from "./userDisplay";

const PAGE_SIZE = 12; // mirrors backend REST_FRAMEWORK PAGE_SIZE

/**
 * Finished tasks that have left the board (projects/task-archive, A9):
 * newest-archived first, searchable. Nothing here is deleted: Restore puts
 * a Done task back in its column, still Done, for another 7 days; a
 * Canceled one is reopened to TODO instead (the board doesn't show those).
 */
function TaskArchive() {
  const { projects } = useProjects();
  const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
  const [tasks, setTasks] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Several restores can be in flight at once; each row tracks its own.
  const [restoringIds, setRestoringIds] = useState(() => new Set());
  const [restored, setRestored] = useState(null);
  // The board's "N archived — view archive" link carries its project.
  const [searchParams, setSearchParams] = useSearchParams();
  // The page lives in the URL too (board #79), beside ?project=.
  const [page, setPage] = usePageParam();
  const projectFilter = Number(searchParams.get("project"));
  const scopedProject =
    Number.isFinite(projectFilter) && projectFilter > 0 ? projectFilter : null;
  const [restoreError, setRestoreError] = useState(null);

  // Only the newest listing may land — its rows, its error, its end of
  // loading. Restores re-list, and several can be in flight: a listing
  // sent before another restore committed still holds that task, and
  // landing last would bring it back (with a Restore that can only 409).
  // Page and search changes race the same way.
  const archiveRequest = useRef(0);
  const fetchArchive = useCallback(async () => {
    const request = ++archiveRequest.current;
    const isLatest = () => request === archiveRequest.current;
    setLoading(true);
    setError(null);
    try {
      const params = { archived: "true", ordering: "-archived_at", page };
      if (search) params.search = search;
      if (scopedProject) params.project = scopedProject;
      const { data } = await taskApi.getAll(params);
      if (!isLatest()) return;
      setTasks(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      if (!isLatest()) return;
      // A page past the end (after restores emptied it): back to page 1.
      if (err?.response?.status === 404 && page > 1) {
        setPage(1, { replace: true });
        return;
      }
      setError(err);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [page, setPage, search, scopedProject]);

  // A restore asks for a re-list by bumping this rather than calling
  // fetchArchive itself: its fetchArchive is the one from the click's
  // render, so a search or page change made while the restore was in
  // flight would be re-listed with the OLD query — and, as the newest
  // request, win. The effect always runs the current query.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    fetchArchive();
  }, [fetchArchive, reloadTick]);

  const handleSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1, { replace: true });
  };

  // Done tasks are restored to the board. Canceled tasks never show on
  // the board, so "restoring" one would put it nowhere visible (and back in
  // the archive a day later) — they're reopened to TODO instead.
  const handleRestore = async (task) => {
    const reopen = task.status === "CANCELED";
    setRestoringIds((prev) => new Set(prev).add(task.id));
    setRestored(null);
    setRestoreError(null);
    try {
      if (reopen) await taskApi.update(task.id, { status: "TODO" });
      else await taskApi.unarchive(task.id);
      setRestored({ ...task, reopened: reopen });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      // Re-list: the page may now be short or past the end (the fetch's
      // 404 self-heal takes it back to page 1).
      setReloadTick((tick) => tick + 1);
    } catch (err) {
      setRestoreError(
        apiErrorMessage(
          err,
          `Couldn't ${reopen ? "reopen" : "restore"} the task.`,
        ),
      );
    } finally {
      setRestoringIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Archive"
        subtitle={`Finished tasks leave the board on their own — Done after ${ARCHIVE_AFTER_DAYS} days, Canceled after ${ARCHIVE_CANCELED_AFTER_DAYS}, counted from when the task first closed — or when someone archives them. Nothing here is deleted: Restore puts a Done task back on the board; Reopen brings a canceled one back as TODO.`}
      />

      <form onSubmit={handleSearch} className="mb-4 flex max-w-xl">
        <Input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search archived tasks by title or description…"
          aria-label="Search archived tasks"
          className="rounded-r-none"
        />
        <Button
          type="submit"
          variant="outline"
          className="rounded-l-none border-l-0"
        >
          Search
        </Button>
        {search && (
          <Button
            type="button"
            variant="outline"
            className="rounded-l-none border-l-0"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPage(1, { replace: true });
            }}
          >
            Clear
          </Button>
        )}
      </form>

      {scopedProject && (
        <p className="mb-3 text-sm text-muted-foreground">
          Showing{" "}
          {projectsById[scopedProject]?.name || `project ${scopedProject}`}{" "}
          only.{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            // One URL write: clearing the params drops ?page= too. A
            // setPage(1) after it would rebuild from the old params and
            // put ?project= back (usePageParam).
            onClick={() => setSearchParams({})}
          >
            Show all projects
          </button>
        </p>
      )}

      {restored && (
        <Alert className="mb-3 py-2" role="status">
          <AlertDescription className="text-sm">
            {/* One inline run: the alert lays out each child on its own row. */}
            <span>
              {restored.reopened ? "Reopened" : "Restored"} “{restored.title}” —
              it&apos;s back on the{" "}
              <Link to="/tasks/board" className="underline underline-offset-2">
                board
              </Link>{" "}
              in {restored.reopened ? "TODO" : statusLabel(restored.status)}.
            </span>
          </AlertDescription>
        </Alert>
      )}
      {restoreError && (
        <Alert variant="destructive" className="mb-3 py-2">
          <AlertDescription className="text-sm">
            {restoreError}
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading the archive…
        </div>
      )}

      {!loading && error && (
        <Alert className="py-2">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1 text-sm">
              Couldn&apos;t load the archive.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={fetchArchive}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && tasks.length === 0 && (
        <p className="text-sm text-muted-foreground italic">
          {search
            ? `No archived tasks match “${search}”.`
            : "Nothing archived yet — finished tasks land here on their own."}
        </p>
      )}

      {!loading && !error && tasks.length > 0 && (
        <ul
          className="list-none divide-y rounded-md border p-0"
          aria-label="Archived tasks"
        >
          {tasks.map((task) => {
            const project =
              task.project != null ? projectsById[task.project] : null;
            return (
              <li
                key={task.id}
                className="flex flex-wrap items-center gap-3 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/tasks/board?task=${task.id}`}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {task.title}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="font-normal">
                      {statusLabel(task.status)}
                    </Badge>
                    {/* Real spaces between the pieces: the flex gap is
                        visual only (mono-frontend-patterns). */}
                    {project && (
                      <>
                        {" "}
                        <span>{project.name}</span>
                      </>
                    )}
                    {task.closed_at && (
                      <>
                        {" "}
                        <span title={task.closed_at}>
                          finished {relativeTime(task.closed_at)}
                        </span>
                      </>
                    )}{" "}
                    <span title={task.archived_at}>
                      archived {relativeTime(task.archived_at)}{" "}
                      {task.archived_by
                        ? `by ${displayName(task.archived_by)}`
                        : "automatically"}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={restoringIds.has(task.id)}
                  aria-label={`${task.status === "CANCELED" ? "Reopen" : "Restore"} ${task.title}`}
                  title={
                    task.status === "CANCELED"
                      ? "Canceled tasks don't show on the board — this reopens it in TODO"
                      : "Put it back on the board, still Done"
                  }
                  onClick={() => handleRestore(task)}
                >
                  {restoringIds.has(task.id)
                    ? "…"
                    : task.status === "CANCELED"
                      ? "Reopen"
                      : "Restore"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && totalPages > 1 && (
        <Pagination
          page={page}
          total={totalPages}
          onChange={setPage}
          className="mt-4"
        />
      )}
    </div>
  );
}

export default TaskArchive;
